import { sectionRenderer } from '@theme/section-renderer';

/**
 * Keeps holiday collection filters, sort, pagination and Back/Forward working.
 *
 * Canonical routing (assigned collection template, clean URL): no view parameter is added.
 * Alternate-view routing (?view=jd-holiday-x) and theme previews (?preview_theme_id=):
 * whichever of those the shopper landed with is carried through every filter, sort and
 * pagination URL, so the holiday template never drops back to the default collection.
 */
class JDHolidayCollection extends HTMLElement {
  #observer;
  #view = '';
  #previewThemeId = '';
  #onInteraction = () => this.#synchronize();

  #onPopState = async () => {
    const url = new URL(window.location.href);
    if (!this.dataset.sectionId || !this.#isCollectionPath(url)) return;
    if ((url.searchParams.get('view') || '') !== this.#view) return;

    const requestedHref = window.location.href;
    this.querySelector('results-list')?.pages?.clear();
    try {
      await sectionRenderer.renderSection(this.dataset.sectionId, {
        url,
        shouldRender: () => window.location.href === requestedHref,
      });
      if (this.isConnected) this.#synchronize();
    } catch {
      if (window.location.href === requestedHref) window.location.reload();
    }
  };

  connectedCallback() {
    const current = new URL(window.location.href);
    this.#view = current.searchParams.get('view') || '';
    this.#previewThemeId = current.searchParams.get('preview_theme_id') || '';

    this.#observer = new MutationObserver(() => this.#synchronize());
    this.#observer.observe(this, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['href', 'data-url', 'action'],
    });
    for (const type of ['click', 'change', 'keydown', 'pointerover', 'pointerdown', 'submit']) {
      this.addEventListener(type, this.#onInteraction, true);
    }
    window.addEventListener('popstate', this.#onPopState);
    this.#synchronize();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    for (const type of ['click', 'change', 'keydown', 'pointerover', 'pointerdown', 'submit']) {
      this.removeEventListener(type, this.#onInteraction, true);
    }
    window.removeEventListener('popstate', this.#onPopState);
  }

  #cleanPath(path) {
    return path.replace(/\/$/, '');
  }

  #isCollectionPath(url) {
    try {
      const collectionUrl = new URL(this.dataset.collectionUrl, window.location.origin);
      return url.origin === window.location.origin && this.#cleanPath(url.pathname) === this.#cleanPath(collectionUrl.pathname);
    } catch {
      return false;
    }
  }

  #context() {
    const parameters = new URLSearchParams();
    if (this.#view) parameters.set('view', this.#view);
    if (this.#previewThemeId) parameters.set('preview_theme_id', this.#previewThemeId);
    return parameters;
  }

  #preserveContext(element, attribute) {
    const original = element.getAttribute(attribute);
    if (!original) return;
    let url;
    try {
      url = new URL(original, window.location.href);
    } catch {
      return;
    }
    if (!this.#isCollectionPath(url)) return;
    for (const [key, value] of this.#context()) url.searchParams.set(key, value);
    const updated = `${url.pathname}${url.search}${url.hash}`;
    if (original !== updated) element.setAttribute(attribute, updated);
  }

  #synchronize() {
    const context = [...this.#context()];
    if (context.length === 0) return;

    for (const form of this.querySelectorAll('facets-form-component form[ref="facetsForm"]')) {
      for (const [name, value] of context) {
        let input = form.querySelector(`input[type="hidden"][name="${name}"]`);
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = name;
          form.append(input);
        }
        if (input.value !== value) input.value = value;
      }
      this.#preserveContext(form, 'action');
    }
    for (const control of this.querySelectorAll('facet-remove-component[data-url]')) {
      this.#preserveContext(control, 'data-url');
    }
    for (const link of this.querySelectorAll('.pagination a[href], .main-collection-grid__empty a[href]')) {
      this.#preserveContext(link, 'href');
    }
  }
}

if (!customElements.get('jd-holiday-collection')) {
  customElements.define('jd-holiday-collection', JDHolidayCollection);
}
